import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Filter, Minus, Pause, Phone, Play, Sparkles, X } from 'lucide-react';
import { getCall, getScriptReview, getTranscript, type CallItem, type ReviewStatus, type TranscriptSegment } from '@/api/calls';
import { Sheet } from '@/components/Sheet';
import { callsKeys } from '@/hooks/useCallsData';
import { useRecordingPlayer } from '@/hooks/useRecordingPlayer';
import { callDayLabel, callName, callTitle, clock, isMissed, isMissedOpen, timeOf } from '@/lib/callsQuery';
import { FALLBACK_STAGE_COLOR } from '@/lib/dealCard';

interface Props {
  initial: CallItem;
  project: string;
  onClose: () => void;
  onCall: (c: CallItem) => void;
  onOpenDeal: (c: CallItem) => void;
}

const who = (s: TranscriptSegment) => (s.role === 'operator' ? 'Сотрудник' : s.role === 'client' ? 'Клиент' : `Голос ${Number(s.speaker.replace(/\D+/g, '') || 0) + 1}`);
const mmss = (ms: number) => clock(Math.max(1, Math.floor(ms / 1000))).replace('—', '0:00');

const OK: ReviewStatus[] = ['done', 'handled', 'clean'];
const NA: ReviewStatus[] = ['na', 'not_raised', 'unclear'];

/**
 * Окно звонка (прототип: callSheet): запись с плеером, «Итог» и следующий шаг, расшифровка по ролям
 * (нажал строку — запись перескочила туда), оценка по скрипту, «Позвонить» / «Открыть сделку».
 * Строка журнала показывается сразу, карточка дочитывается с сервера.
 */
export function CallSheet({ initial, project, onClose, onCall, onOpenDeal }: Props) {
  const card = useQuery({ queryKey: callsKeys.card(project, initial.id), queryFn: () => getCall(initial.id), staleTime: 30_000 });
  const c: CallItem = card.data ?? initial;
  const trDone = c.transcriptState === 'done';
  const transcript = useQuery({
    queryKey: callsKeys.transcript(project, c.id), enabled: trDone, queryFn: () => getTranscript(c.id), staleTime: 5 * 60_000,
  });
  const review = useQuery({
    queryKey: callsKeys.review(project, c.id), enabled: trDone, queryFn: () => getScriptReview(c.id), staleTime: 5 * 60_000, retry: false,
  });
  const hasRec = c.recordingState === 'stored';
  const p = useRecordingPlayer(c.id, c.durationSeconds ?? 0);
  const barRef = useRef<HTMLDivElement | null>(null);

  const name = callName(c);
  const nowMs = p.time * 1000;
  const segs = transcript.data?.segments ?? [];
  const summary = c.summary ?? transcript.data?.summary ?? null;
  const next = c.nextStep ?? transcript.data?.nextStep ?? null;
  const dur = p.duration || c.durationSeconds || 0;

  const seekBar = (e: React.PointerEvent) => {
    const el = barRef.current;
    if (!el || !dur) return;
    const r = el.getBoundingClientRect();
    void p.seek(Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)) * dur, p.playing);
  };

  const score = review.data?.manual?.score ?? review.data?.score ?? c.scriptScore;
  const items = review.data?.items ?? [];

  return (
    <Sheet label={`Звонок: ${name}`} onClose={onClose} tall>
      <div className="sh">
        <h3>{name}</h3>
        <span className="shsub tnum">{callDayLabel(c.occurredAt)}, {timeOf(c.occurredAt)}</span>
      </div>
      <div className="sb">
        <div className="cmeta">
          <span className="sdot" style={{ background: c.stageColor || FALLBACK_STAGE_COLOR }} aria-hidden />
          <span>{callTitle(c)}{c.agentName ? `, ${c.agentName}` : ''}{c.stageName ? ` · ${c.stageName}` : ''}</span>
        </div>

        {hasRec && (
          <div className="player">
            <button type="button" className="pbtn" onClick={() => void p.toggle()} aria-label={p.playing ? 'Пауза' : 'Слушать запись'} disabled={p.loading}>
              {p.loading ? <span className="spinner size-5 inline-block" /> : p.playing ? <Pause aria-hidden /> : <Play aria-hidden />}
            </button>
            <div className="ptrack">
              <div className="pbar" ref={barRef} onPointerDown={seekBar} role="slider" aria-label="Перемотка записи"
                aria-valuemin={0} aria-valuemax={Math.round(dur)} aria-valuenow={Math.round(p.time)} tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowRight') void p.seek(p.time + 5, p.playing);
                  if (e.key === 'ArrowLeft') void p.seek(p.time - 5, p.playing);
                }}>
                <i style={{ width: `${dur ? Math.min(100, (p.time / dur) * 100) : 0}%` }} />
              </div>
              <div className="ptime tnum"><span>{clock(p.time) === '—' ? '0:00' : clock(p.time)}</span><span>{clock(dur)}</span></div>
            </div>
          </div>
        )}
        {p.error && <p className="ferr" role="alert">{p.error}</p>}
        {!hasRec && c.outcome === 'answered' && (
          <div className="box">{c.recordingState === 'pending' ? 'Запись ещё копируется — появится здесь сама.' : c.recordingState === 'failed' ? `Запись не скопирована${c.recordingError ? `: ${c.recordingError}` : ''}.` : 'АТС не прислала запись этого разговора.'}</div>
        )}
        {isMissed(c) && (
          <div className="box">{isMissedOpen(c) ? 'Клиент звонил и не дозвонился. Ему ещё не перезвонили — в календаре стоит задача «Перезвонить».' : `Клиенту уже перезвонили${c.callback?.byName ? ` (${c.callback.byName})` : ''}.`}</div>
        )}
        {c.direction === 'outgoing' && c.outcome !== 'answered' && c.outcome !== 'live' && <div className="box">Клиент не взял трубку. Записи нет.</div>}

        {(summary || next) && (
          <div className="box">
            <div className="h4"><Sparkles size={16} style={{ color: 'var(--accent)' }} aria-hidden />Итог</div>
            {summary && <span>{summary}</span>}
            {next && <div className="nx">Следующий шаг: {next}</div>}
          </div>
        )}

        {c.outcome === 'answered' && (
          <div>
            <div className="h4" style={{ marginBottom: 4 }}>Расшифровка</div>
            {c.transcriptDraft && trDone && (
              <div className="box warn" style={{ marginBottom: 6 }}>Черновая расшифровка: узбекская или смешанная речь распознаётся слабее — сверяйте с записью.</div>
            )}
            {trDone ? (
              transcript.isPending ? <div className="sk" style={{ height: 80 }} aria-label="Загружаем расшифровку" />
                : transcript.isError ? <p className="ferr">{transcript.error.message}</p>
                  : segs.length === 0 ? <p className="pnote">В расшифровке нет реплик.</p>
                    : (
                      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Расшифровка по репликам">
                        {segs.map((s, i) => {
                          const cur = p.time > 0 && nowMs >= s.startMs && nowMs < s.endMs;
                          return (
                            <li key={i}>
                              <button type="button" className={`line${cur ? ' cur' : ''}`} disabled={!hasRec}
                                aria-current={cur ? 'true' : undefined}
                                aria-label={`Перемотать на ${mmss(s.startMs)}: ${who(s)}`}
                                onClick={() => void p.seek(s.startMs / 1000)}>
                                <span className={`who2${s.role === 'operator' ? ' e' : s.role === 'client' ? ' c' : ''}`}>{who(s)}</span>
                                <span style={{ flex: 1 }}>{s.text}</span>
                                <span className="ts tnum">{mmss(s.startMs)}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ol>
                    )
            ) : (
              <p className="pnote">
                {c.transcriptState === 'pending' ? `Расшифровывается — появится здесь сама.${c.transcriptError ? ` ${c.transcriptError}` : ''}`
                  : c.transcriptState === 'failed' ? `Расшифровать не удалось${c.transcriptError ? `: ${c.transcriptError}` : ''}.`
                    : c.transcriptState === 'skipped' ? 'Короткий разговор — не расшифровывали.'
                      : 'Расшифровки нет.'}
              </p>
            )}
          </div>
        )}

        {trDone && review.data && review.data.state === 'done' && (
          <div className="box">
            <div className="score">
              <span className="ring" style={{ ['--p' as string]: score ?? 0 }}><b className="tnum">{score ?? '—'}</b></span>
              <div>
                <div className="h4">Оценка по скрипту</div>
                {review.data.stageName && <div style={{ fontSize: 13, color: 'var(--muted)' }}>Скрипт стадии «{review.data.stageName}»</div>}
              </div>
            </div>
            {items.map((it) => {
              const cls = OK.includes(it.status) ? 'ok' : NA.includes(it.status) ? 'na' : 'no';
              return (
                <div key={it.id} className={`chk ${cls}`}>
                  {cls === 'ok' ? <Check aria-label="выполнено" /> : cls === 'na' ? <Minus aria-label="не оценивалось" /> : <X aria-label="пропущено" />}
                  {it.text}
                </div>
              );
            })}
          </div>
        )}
        {trDone && review.data && (review.data.state === 'pending') && <p className="pnote">Оценка по скрипту готовится.</p>}

        <div className="btnrow">
          <button type="button" className="primary" onClick={() => onCall(c)} disabled={!c.phone}><Phone aria-hidden />Позвонить</button>
          <button type="button" className="ghost" onClick={() => onOpenDeal(c)} disabled={!c.dealId}><Filter aria-hidden />Открыть сделку</button>
        </div>
      </div>
    </Sheet>
  );
}
